import { useEffect, useState } from "react"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Typography from "@mui/material/Typography"
import type { Ape } from "./useOwnedApes"
import { KINDS, render, type Kind } from "./renderImage"

// Holder perks: share card, GIF, wallpaper and banner, drawn in the browser (no server, no cost)
const Downloads = ({ ape, dna }: { ape: Ape, dna: string }) => {
	const [kind, setKind] = useState<Kind>()
	const [file, setFile] = useState<{ blob?: Blob, url?: string, error?: string }>({})

	useEffect(() => {
		if (!kind) return
		let url: string | undefined
		setFile({})
		render(ape, dna, kind)
			.then(blob => setFile({ blob, url: url = URL.createObjectURL(blob) }))
			.catch((e: Error) => setFile({ error: e.message }))
		return () => { if (url) URL.revokeObjectURL(url) }
	}, [ape, dna, kind])

	const name = kind && `${ape.name.replace(/\W+/g, "-").toLowerCase()}-${kind}.${KINDS[kind].ext}`
	const shareFile = file.blob && name ? new File([file.blob], name, { type: file.blob.type }) : undefined
	// Mobile share sheet (save to Photos, post to X…); runs on its own tap so the browser allows it
	const canShare = !!shareFile && !!navigator.canShare?.({ files: [shareFile] })

	return (
		<>
			<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: .5 }}>
				Holder downloads
			</Typography>
			<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: .5 }}>
				{(Object.keys(KINDS) as Kind[]).map(k => (
					<Button key={k} size="small" variant="outlined" onClick={() => setKind(k)} sx={{ borderRadius: 999 }}>
						{KINDS[k].label}
					</Button>
				))}
			</Box>
			<Dialog open={!!kind} onClose={() => setKind(undefined)} maxWidth="sm" fullWidth>
				{kind && <>
					<DialogTitle>{KINDS[kind].label}<Typography color="text.secondary">{KINDS[kind].hint}</Typography></DialogTitle>
					<DialogContent sx={{ textAlign: "center" }}>
						{file.error && <Alert severity="error">{file.error}</Alert>}
						{!file.url && !file.error && <CircularProgress sx={{ my: 6 }} />}
						{file.url && <Box component="img" src={file.url} alt={`${ape.name} ${KINDS[kind].label}`}
							sx={{ maxWidth: "100%", maxHeight: "60vh", borderRadius: 1, boxShadow: "0 10px 30px rgba(43,45,66,.15)" }} />}
					</DialogContent>
					<DialogActions sx={{ px: 3, pb: 2.5 }}>
						<Button onClick={() => setKind(undefined)}>Close</Button>
						{canShare && <Button variant="outlined" onClick={() => shareFile && navigator.share({ files: [shareFile] }).catch(() => undefined)}>Share</Button>}
						<Button component="a" variant="contained" disabled={!file.url} href={file.url ?? ""} download={name}>Download</Button>
					</DialogActions>
				</>}
			</Dialog>
		</>
	)
}

export default Downloads
